// consts/enums
import * as types from '../types/actionTypes';
import { KEYID_DELIMETER } from '../types/commonTypes';
import type { PenciledInputsState } from '../types/commonTypes';

// utils
import { initialState } from './initialState';
import { mergeIncentives } from '../utils';
import { parseIncentiveId } from '../utils/incentives';
import { incentiveUtils } from '@makemydeal/dr-common-utils';
import { CASH } from '@makemydeal/dr-platform-types';
import type { GenericObject, OfferIncentives, OfferType } from '@makemydeal/dr-platform-types';
import type { AnyAction } from 'redux';

const dedupeList = <T>(values: T[]): T[] => Array.from(new Set(values));

const isPresent = (value: unknown): boolean => value != null;

type IdListNormalizer = <T>(values: T[] | undefined) => T[] | undefined;

const dedupeIds: IdListNormalizer = (values) => (Array.isArray(values) ? dedupeList(values) : values);

const dedupeAndDropNullIds: IdListNormalizer = (values) => (Array.isArray(values) ? dedupeList(values.filter(isPresent)) : values);

const pickIdListNormalizer = (skipNullIds: boolean): IdListNormalizer => (skipNullIds ? dedupeAndDropNullIds : dedupeIds);

const normalizeOfferTypeIncentives = (
    entry: GenericObject | undefined,
    normalizeIdList: IdListNormalizer
): GenericObject | undefined =>
    entry == null
        ? entry
        : {
              ...entry,
              conditionals: normalizeIdList(entry.conditionals as unknown[] | undefined),
              nonConditionals: normalizeIdList(entry.nonConditionals as unknown[] | undefined)
          };

const normalizeByOfferType = (
    byOfferType: GenericObject | undefined,
    normalizeIdList: IdListNormalizer
): GenericObject | undefined =>
    byOfferType == null
        ? byOfferType
        : Object.fromEntries(
              Object.entries(byOfferType).map(([offerType, entry]) => [
                  offerType,
                  normalizeOfferTypeIncentives(entry as GenericObject | undefined, normalizeIdList)
              ])
          );

const normalizeAppliedKeyLists = (applied: GenericObject, normalizeIdList: IdListNormalizer): GenericObject => ({
    ...applied,
    conditionals: normalizeIdList(applied.conditionals as unknown[] | undefined),
    nonConditionals: normalizeIdList(applied.nonConditionals as unknown[] | undefined)
});

export const normalizeLoadedPaymentIncentives = <T>(payment: T, skipNullIds = false): T => {
    const node = payment as GenericObject | undefined;
    if (node == null) {
        return payment;
    }
    const normalizeIdList = pickIdListNormalizer(skipNullIds);
    return {
        ...node,
        ...(node.appliedIncentiveIds && { appliedIncentiveIds: normalizeIdList(node.appliedIncentiveIds as unknown[]) }),
        ...(node.appliedIncentives && {
            appliedIncentives: normalizeAppliedKeyLists(node.appliedIncentives as GenericObject, normalizeIdList)
        })
    } as T;
};

export const normalizeLoadedIncentives = (
    incentives: GenericObject | undefined,
    skipNullIds = false
): GenericObject | undefined => {
    if (incentives == null) {
        return incentives;
    }
    const normalizeIdList = pickIdListNormalizer(skipNullIds);
    return {
        ...incentives,
        allIds: normalizeIdList(incentives.allIds as unknown[] | undefined),
        byOfferType: normalizeByOfferType(incentives.byOfferType as GenericObject | undefined, normalizeIdList)
    };
};

// Cash has no term-level catalog, and its checkbox-toggle recalc always resolves
// includeIncentives: false (it isn't a "program catalog" action) - so the response's own
// incentives node is thin/stale and not worth reading. The penciled cash catalog, by
// contrast, is refreshed unconditionally on every cash PAYMENT_RECEIVED and already carries
// the full incentive detail object, so it's used as the sole merge source instead of
// action.payload.incentives, which would otherwise leave offer.incentives.byId permanently
// missing any conditional the program-orchestrator discovered later (that discovery only
// ever writes into the penciled cash catalog via UPDATE_CASH_AVAILABLE_INCENTIVES, never
// into this node).
const getPenciledCashCatalog = (penciledInputs: PenciledInputsState | undefined): GenericObject =>
    penciledInputs?.byOfferType?.cash?.incentives ?? {};

const buildCashIncentivesFromPenciledCatalog = (
    penciledInputs: PenciledInputsState | undefined,
    previousById: GenericObject = {}
): OfferIncentives => {
    const catalog = getPenciledCashCatalog(penciledInputs);
    const byId: GenericObject = {};
    const toNumericIds = (bucket: GenericObject[] | undefined): number[] =>
        (bucket ?? []).reduce<number[]>((ids, incentive) => {
            if (!incentive.id) {
                return ids;
            }
            const numericId = parseIncentiveId(incentive.id);
            if (!Number.isNaN(numericId)) {
                byId[incentive.id] = { ...previousById[incentive.id], ...incentive, pdId: numericId };
                ids.push(numericId);
            }
            return ids;
        }, []);

    return {
        byId,
        allIds: Object.keys(byId),
        byOfferType: {
            cash: {
                conditionals: toNumericIds(catalog.conditionals as GenericObject[] | undefined),
                nonConditionals: toNumericIds(catalog.nonConditionals as GenericObject[] | undefined)
            }
        },
        mappings: {}
    } as unknown as OfferIncentives;
};

const NON_FILLABLE_INCENTIVE_KEYS = new Set(['id', 'pdId', 'amount']);

const fillMissingDetail = (existing: GenericObject, catalogEntry: GenericObject): GenericObject | undefined => {
    const missingKeys = Object.keys(catalogEntry).filter(
        (key) => !NON_FILLABLE_INCENTIVE_KEYS.has(key) && existing[key] == null && catalogEntry[key] != null
    );
    if (!missingKeys.length) {
        return undefined;
    }
    return missingKeys.reduce<GenericObject>(
        (filled, key) => {
            filled[key] = catalogEntry[key];
            return filled;
        },
        { ...existing }
    );
};

const fillCashIncentiveDetailFromPenciledCatalog = (
    state: OfferIncentives,
    penciledInputs: PenciledInputsState | undefined
): OfferIncentives => {
    const catalog = getPenciledCashCatalog(penciledInputs);
    const byId: GenericObject = state.byId ?? {};
    const catalogEntries = [...(catalog.conditionals ?? []), ...(catalog.nonConditionals ?? [])] as GenericObject[];
    const matchingCatalogEntries = catalogEntries.filter((entry) => entry.id && byId[entry.id]);
    const filledById = matchingCatalogEntries.reduce<GenericObject>((acc, entry) => {
        const filled = fillMissingDetail(byId[entry.id], entry);
        if (filled) {
            acc[entry.id] = filled;
        }
        return acc;
    }, {});
    if (!Object.keys(filledById).length) {
        return state;
    }
    return { ...state, byId: { ...byId, ...filledById } } as OfferIncentives;
};

export function buildUpdatedIncentivesState(
    state: OfferIncentives = initialState.incentives,
    action: AnyAction,
    offerType: OfferType,
    programIncentivesRestorationEnabled = false,
    penciledInputs?: PenciledInputsState,
    conditionalIncentivesTotalCashDealLoadEnabled = false,
    skipNullCashIncentiveIdsEnabled = false,
    keepCashIncentiveCatalogDetailEnabled = false,
    cashIncentiveNameOnCatalogLoadEnabled = false
): OfferIncentives {
    switch (action.type) {
        case types.INIT_OFFER:
            return { ...state, ...normalizeLoadedIncentives(action.payload.incentives, skipNullCashIncentiveIdsEnabled) };
        case types.PAYMENT_RECEIVED:
            if (action.meta?.isStalePaymentResponse) {
                return state;
            }
            if (offerType === CASH && conditionalIncentivesTotalCashDealLoadEnabled && action.meta?.includeIncentives === false) {
                if (keepCashIncentiveCatalogDetailEnabled) {
                    return mergeIncentives(
                        state,
                        buildCashIncentivesFromPenciledCatalog(penciledInputs, state.byId),
                        offerType,
                        skipNullCashIncentiveIdsEnabled
                    );
                }
                return mergeIncentives(
                    state,
                    buildCashIncentivesFromPenciledCatalog(penciledInputs),
                    offerType,
                    skipNullCashIncentiveIdsEnabled
                );
            }
            if (programIncentivesRestorationEnabled && action.meta?.includeIncentives === false) {
                return state;
            }
            return mergeIncentives(state, action.payload?.incentives, offerType, skipNullCashIncentiveIdsEnabled);
        case types.SYNC_MESSAGE:
            return {
                ...state,
                ...normalizeLoadedIncentives(action.message.offer.incentives, skipNullCashIncentiveIdsEnabled)
            };
        case types.UPDATE_CASH_AVAILABLE_INCENTIVES:
            return cashIncentiveNameOnCatalogLoadEnabled
                ? fillCashIncentiveDetailFromPenciledCatalog(state, penciledInputs)
                : state;
        case types.UPDATE_AVAILABLE_INCENTIVES: {
            const newState = incentiveUtils.mapIncentivesForOffer(action.payload || {});
            return newState;
        }
        // Populate mappings directly from the incentives applied with the program so that
        // getMappedIncentives can resolve IDs even on a fresh deal with no prior payments.
        case types.APPLIED_PROGRAM: {
            const incentives = action.payload?.incentives as string[] | undefined;
            if (!incentives?.length) return state;

            const newMappings: Record<string, number> = {};
            for (const incentive of incentives) {
                // Format: 'conditional_finance_123' or 'nonconditional_finance_123'
                const lastUnderscoreIdx = incentive.lastIndexOf(KEYID_DELIMETER);
                if (lastUnderscoreIdx >= 0) {
                    const id = parseInt(incentive.substring(lastUnderscoreIdx + 1), 10);
                    if (!isNaN(id)) {
                        newMappings[incentive] = id;
                    }
                }
            }

            return { ...state, mappings: { ...state.mappings, ...newMappings } };
        }
        default:
            return state;
    }
}
